// UPPDRAG-19 PR-B — anonymous schedule read.
//
// Read-only schedule visible to anyone who hits /book or / without
// being logged in. Uses the existing <BookingGrid> for rendering;
// data comes from anon-readable surfaces (per the migration shipped
// alongside this component):
//   - public.courts                 (anon SELECT, filter by hall_id)
//   - public.halls                  (anon SELECT, filter by org_id)
//   - public.pricing_rules          (anon SELECT)
//   - public.special_price_dates    (anon SELECT)
//   - public.court_sponsors         (anon SELECT)
//   - public.public_bookings_view   (security_barrier=true, no PII)
//
// Click handling per Rickard's plan:
//   - Empty slot → triggers AuthRequiredModal (UPPDRAG-19 PR-A) with
//     intent='book_slot' and returnUrl=current path. After signup/
//     login, user lands back on /book authenticated.
//   - Booked slot → no action (simplest UX; can add a "Bokad" tooltip
//     later if needed).
//
// landing_events analytics (Q5):
//   - On mount, INSERT a row with event_type='anon_schedule_view',
//     hostname, organization_id, user_agent. Best-effort — never blocks
//     render. Authenticated users skip this insert (the route's anon
//     mode is the only path that hits this component).

import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, Link } from "react-router-dom";
import { format, startOfDay } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { usePublicOrgContext } from "@/contexts/PublicOrgContext";
import { useAuthRequired } from "@/contexts/AuthRequiredContext";
import { getLocalDayBounds } from "@/lib/utils";
import { type PricingRule, type SpecialPriceDate } from "@/lib/pricing";
import { activeSponsorsSorted, type CourtSponsorRow } from "@/lib/courtSponsorHelpers";
import BookingGrid from "@/components/booking/BookingGrid";
import DateStrip from "@/components/shared/DateStrip";
import { Card, CardContent } from "@/components/ui/card";
import { Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";

interface PublicBookingRow {
  id: string;
  court_id: string;
  start_time: string;
  end_time: string;
  duration_minutes: number;
  status: string;
  is_open_match: boolean | null;
  activity_id: string | null;
  // price_total / price_total_minor optional — staging schema
  // doesn't have the minor variant; PROD does.
  price_total?: number | null;
  price_total_minor?: number | null;
}

export default function AnonymousBook() {
  const { t } = useTranslation("booking");
  const navigate = useNavigate();
  const { org: publicOrg } = usePublicOrgContext();
  const { requireAuth } = useAuthRequired();
  const [selectedDate, setSelectedDate] = useState(startOfDay(new Date()));

  // ── 1. Hall (anon-readable via "Anon read halls" RLS) ──
  const { data: hall, isLoading: hallLoading } = useQuery({
    queryKey: ["anon-hall", publicOrg?.id],
    queryFn: async () => {
      if (!publicOrg?.id) return null;
      const { data, error } = await supabase
        .from("halls")
        .select("id, name, opening_time, closing_time, time_step_minutes, min_duration_minutes, max_duration_minutes")
        .eq("organization_id", publicOrg.id)
        .order("created_at", { ascending: true })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!publicOrg?.id,
    staleTime: 5 * 60_000,
  });

  // ── 2. Courts (anon-readable via "Anon read courts" RLS) ──
  const { data: courts = [] } = useQuery({
    queryKey: ["anon-courts", hall?.id],
    queryFn: async () => {
      if (!hall?.id) return [];
      const { data, error } = await supabase
        .from("courts")
        .select("id, name, court_type, surface_type, max_players, status, sort_order")
        .eq("hall_id", hall.id)
        .eq("status", "active")
        .order("sort_order");
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!hall?.id,
    staleTime: 5 * 60_000,
  });

  const courtIds = useMemo(() => courts.map((c) => c.id), [courts]);

  // ── 3. Bookings — via the SECURITY-BARRIER VIEW (no PII columns) ──
  // Q4: snapshot at page-load only (no real-time subscription).
  const { data: bookings = [] } = useQuery({
    queryKey: ["anon-bookings", hall?.id, selectedDate.toISOString()],
    queryFn: async () => {
      if (!hall?.id || courtIds.length === 0) return [];
      const { start, end } = getLocalDayBounds(selectedDate);
      const { data, error } = await supabase
        .from("public_bookings_view" as any)
        .select("id, court_id, start_time, end_time, duration_minutes, status, is_open_match, activity_id, price_total")
        .in("court_id", courtIds)
        .gte("start_time", start)
        .lte("start_time", end);
      if (error) throw error;
      return (data ?? []) as unknown as PublicBookingRow[];
    },
    enabled: !!hall?.id && courtIds.length > 0,
    staleTime: 60_000,
  });

  // ── 4. Pricing (Q3 — show prices to anonymous visitors) ──
  const { data: pricingRules = [] } = useQuery({
    queryKey: ["anon-pricing-rules", hall?.id],
    queryFn: async () => {
      if (!hall?.id) return [];
      const { data, error } = await supabase
        .from("pricing_rules")
        .select("*")
        .eq("hall_id", hall.id);
      if (error) throw error;
      return (data ?? []) as PricingRule[];
    },
    enabled: !!hall?.id,
    staleTime: 5 * 60_000,
  });

  const { data: specialDates = [] } = useQuery({
    queryKey: ["anon-special-dates", publicOrg?.id],
    queryFn: async () => {
      if (!publicOrg?.id) return [];
      const { data, error } = await supabase
        .from("special_price_dates" as any)
        .select("*")
        .eq("organization_id", publicOrg.id);
      if (error) throw error;
      return (data ?? []) as unknown as SpecialPriceDate[];
    },
    enabled: !!publicOrg?.id,
    staleTime: 5 * 60_000,
  });

  // ── 5b. TASK-75 PR-D Layer 1 — Placed league_matches (anon-readable) ──
  // Surfaces placed (but not-yet-converted-to-booking) league_matches as
  // visually blocked slots so anonymous visitors can't waste a click on
  // them. Layer 4 DB trigger guards the actual INSERT regardless.
  const dateStr = useMemo(() => format(selectedDate, "yyyy-MM-dd"), [selectedDate]);
  const { data: leagueMatches = [] } = useQuery({
    queryKey: ["anon-league-matches", hall?.id, dateStr],
    queryFn: async () => {
      if (!hall?.id) return [];
      const { data, error } = await supabase
        .from("league_matches")
        .select(
          "id, court_id, scheduled_date, scheduled_time, status, leagues!inner(id, match_duration_minutes, hall_id)",
        )
        .eq("scheduled_date", dateStr)
        .not("scheduled_time", "is", null)
        .neq("status", "cancelled")
        .eq("leagues.hall_id", hall.id);
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!hall?.id,
    staleTime: 60_000,
  });

  // ── 5. Court sponsors (UPPDRAG-15 + anon SELECT from this PR) ──
  const { data: rawCourtSponsors = [] } = useQuery({
    queryKey: ["anon-court-sponsors", hall?.id, courtIds.join(",")],
    queryFn: async () => {
      if (courtIds.length === 0) return [];
      const { data, error } = await supabase
        .from("court_sponsors" as any)
        .select("id, court_id, sponsor_name, logo_url, click_url, active_from, active_to, display_order")
        .in("court_id", courtIds);
      if (error) throw error;
      return (data ?? []) as unknown as CourtSponsorRow[];
    },
    enabled: courtIds.length > 0,
    staleTime: 60_000,
  });

  const courtSponsorsByCourtId = useMemo(() => {
    const today = new Date();
    const map: Record<string, CourtSponsorRow[]> = {};
    for (const cid of courtIds) {
      const forCourt = rawCourtSponsors.filter((s) => s.court_id === cid);
      const active = activeSponsorsSorted(forCourt, today);
      if (active.length > 0) map[cid] = active;
    }
    return map;
  }, [courtIds, rawCourtSponsors]);

  // ── 6. landing_events analytics (Q5) — fire-and-forget on mount ──
  useEffect(() => {
    if (!publicOrg?.id) return;
    // PostgrestFilterBuilder is a thenable, not a Promise — chain via
    // .then() and ignore errors silently (best-effort analytics).
    supabase
      .from("landing_events" as any)
      .insert({
        event_type: "anon_schedule_view",
        hostname: typeof window !== "undefined" ? window.location.hostname : null,
        organization_id: publicOrg.id,
        user_agent: typeof navigator !== "undefined" ? navigator.userAgent.slice(0, 500) : null,
      } as any)
      .then(() => {
        /* analytics succeeded; nothing to do */
      });
    // Run once per org-resolution (page-load level granularity is fine)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publicOrg?.id]);

  // ── 7. Hall hours derivation ──
  const hallHours = useMemo(() => {
    if (!hall) return null;
    const close = parseInt(hall.closing_time?.split(":")[0] ?? "0", 10);
    const open = parseInt(hall.opening_time?.split(":")[0] ?? "0", 10);
    return { start: open, end: close };
  }, [hall]);

  // ── 8. Click handlers — both gated through AuthRequiredModal ──
  const handleSlotClick = (_courtId: string, _startTime: Date) => {
    // Trigger conversion modal. After auth, user lands back on /book.
    requireAuth({ intent: "book_slot", returnUrl: "/book" });
  };

  // bookings shape adapter (BookingGrid expects status/user_id; we
  // never have user_id for anon, but BookingGrid only uses it for the
  // `mine` check which is always false here). Cast to `any` rather
  // than fight the type — view rows are intentionally narrower than
  // the table type.
  const allBookings = useMemo(
    () =>
      bookings.map((b) => ({
        ...b,
        user_id: "", // BookingGrid expects a string; anon never has a match
      })),
    [bookings],
  );

  if (hallLoading || !hallHours) {
    return (
      <div className="flex justify-center items-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!hall) {
    return (
      <Card className="m-4">
        <CardContent className="p-8 text-center">
          <p className="text-muted-foreground">
            {t("anonymous.noHall", "Det finns inga banor att visa just nu.")}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4 pb-20 lg:pb-6 max-w-7xl mx-auto p-4">
      <div>
        <div className="flex items-center justify-between gap-2">
          <h1 className="text-2xl font-bold mb-1">
            {publicOrg?.name ?? t("book", "Boka")}
          </h1>
          {/* G-071/G-038: cross-link between the two anonymous surfaces (header
              only — the schedule cells belong to T7) */}
          <Link to="/aktiviteter" className="text-sm font-medium text-primary hover:underline shrink-0">
            {t("anonymousOpenMatchesLink")} →
          </Link>
        </div>
        <p className="text-sm text-muted-foreground">
          {t(
            "anonymous.readOnlyHint",
            "Bläddra i schemat. Skapa konto för att boka en tid.",
          )}
        </p>
      </div>

      <DateStrip
        selectedDate={selectedDate}
        onSelect={(d) => d && setSelectedDate(d)}
        organizationId={publicOrg?.id}
        days={14}
      />

      <BookingGrid
        courts={courts}
        bookings={allBookings as any}
        hallHours={hallHours}
        selectedDate={selectedDate}
        userId={undefined /* anon — no `mine` highlighting */}
        timeStepMinutes={hall.time_step_minutes ?? 15}
        onSlotClick={handleSlotClick}
        /* G-018: clicking an open-match cell as anon opens the auth modal too
           (empty-slot clicks already do via onSlotClick). Without this the
           block's onBookingClick was undefined → the tap did nothing. */
        onBookingClick={() => requireAuth({ intent: "book_slot", returnUrl: "/book" })}
        pricingRules={pricingRules}
        specialDates={specialDates}
        hallId={hall.id}
        courtSponsors={courtSponsorsByCourtId}
        /* TASK-75 PR-D: placed league matches as visually-blocked slots */
        leagueMatches={leagueMatches}
      />

      {/* meta noindex — operational page, not marketing surface */}
      <NoIndexMeta />

      {/* Bottom CTA: explicit "Skapa konto" button below the grid for
          users who scrolled past the schedule and haven't clicked any
          empty slot. */}
      <div className="flex justify-center pt-4">
        <button
          type="button"
          onClick={() => navigate("/auth?mode=signup&returnUrl=/book")}
          className="inline-flex items-center justify-center gap-2 rounded-md bg-primary text-primary-foreground px-6 py-2 text-sm font-medium hover:bg-primary/90 transition-colors"
        >
          {t("anonymous.signupCta", "Skapa konto för att boka")}
        </button>
      </div>
    </div>
  );
}

function NoIndexMeta() {
  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex";
    document.head.appendChild(meta);
    return () => {
      document.head.removeChild(meta);
    };
  }, []);
  return null;
}
